import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Alert,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Stack,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  CircularProgress,
  ListItem
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent} from '@mui/lab';
import {
  Assessment,
  Timeline as TimelineIcon,
  Security,
  VerifiedUser,
  Download,
  Refresh,
  FilterList,
  Search,
  Visibility,
  Edit,
  Delete,
  Add,
  ExpandMore,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
  BugReport,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  BarChart,
  PieChart,
  ShowChart,
  TableChart,
  Dashboard,
  Speed,
  Lock,
  Shield,
  VpnKey,
  Person,
  Business,
  Schedule,
  Payment,
  Inventory,
  Settings,
  Notifications,
  Email,
  Sms,
  Webhook,
  CloudDownload,
  CloudUpload,
  Backup,
  Restore,
  Monitor,
  Api,
  Build,
  Engineering,
  Handyman,
  Construction,
  AutoFixHigh,
  AutoFixNormal,
  AutoFixOff,
  HomeRepairService,
  Plumbing,
  ElectricalServices,
  Carpenter,
  CleaningServices,
  PestControl,
  LocalFireDepartment,
  LocalHospital,
  LocalPharmacy,
  LocalPizza,
  LocalTaxi,
  LocalGasStation,
  LocalCarWash,
  LocalLaundryService,
  LocalLibrary,
  LocalMovies,
  LocalPlay,
  LocalPostOffice,
  LocalSee,
  School,
  Work,
  Store,
  Storefront,
  LocalGroceryStore,
  AccountBalance,
  CreditCard,
  Receipt,
  MonetizationOn,
  People,
  Star,
  CalendarToday,
  History,
  Analytics,
  Print,
  Share,
  GetApp,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Timeline as TimelineIconAlt,
  TableChart as TableChartIcon,
  Dashboard as DashboardIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as ShowChartIcon,
  PlayArrow} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { 
  auditService, 
  AuditLog, 
  AuditFilters, 
  AuditStats, 
  AuditReport, 
  AuditAlertRule, 
  ComplianceCheck 
} from '@services/auditService';
import KPICard from '@shared/KPICard';
import StatusChip from '@shared/StatusChip';
import EmptyState from '@shared/EmptyState';
import SkeletonLoader from '@shared/SkeletonLoader';
import { formatForDisplay, formatDateOnly, formatTimeOnly } from '@utils/dateFormatter';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ReportsManagement: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Audit logs
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [filters, setFilters] = useState<AuditFilters>({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState<number>(0);

  // Setari
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [reports, setReports] = useState<AuditReport[]>([]);
  const [alertRules, setAlertRules] = useState<AuditAlertRule[]>([]);
  const [complianceChecks, setComplianceChecks] = useState<ComplianceCheck[]>([]);

  // Dialogs
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [alertRuleDialogOpen, setAlertRuleDialogOpen] = useState(false);
  const [complianceDialogOpen, setComplianceDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [editingReport, setEditingReport] = useState<AuditReport | null>(null);
  const [editingAlertRule, setEditingAlertRule] = useState<AuditAlertRule | null>(null);
  const [editingCompliance, setEditingCompliance] = useState<ComplianceCheck | null>(null);

  // Incarcare audit logs
  const loadAuditLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await auditService.getAuditLogs({
        ...filters,
        page,
        limit: rowsPerPage
      });
      setAuditLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error loading audit logs:', err);
      setError('Eroare la incarcarea logurilor de audit');
      setAuditLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  // Incarcare statistici
  const loadStats = useCallback(async () => {
    try {
      const data = await auditService.getAuditStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, []);

  // Incarcare rapoarte
  const loadReports = useCallback(async () => {
    try {
      const data = await auditService.getAuditReports();
      setReports(data.reports);
    } catch (err) {
      console.error('Error loading reports:', err);
    }
  }, []);

  // Incarcare reguli de alerta
  const loadAlertRules = useCallback(async () => {
    try {
      const data = await auditService.getAuditAlertRules();
      setAlertRules(data.rules);
    } catch (err) {
      console.error('Error loading alert rules:', err);
    }
  }, []);

  // Incarcare compliance
  const loadComplianceChecks = useCallback(async () => {
    try {
      const data = await auditService.getComplianceChecks();
      setComplianceChecks(data.checks);
    } catch (err) {
      console.error('Error loading compliance checks:', err);
    }
  }, []);

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  useEffect(() => {
    if (activeTab === 1) {
      loadStats();
    } else if (activeTab === 2) {
      loadReports();
    } else if (activeTab === 3) {
      loadAlertRules();
    } else if (activeTab === 4) {
      loadComplianceChecks();
    }
  }, [activeTab, loadStats, loadReports, loadAlertRules, loadComplianceChecks]);

  // Handlers
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleFilterChange = (key: keyof AuditFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleViewLog = (log: AuditLog) => {
    setSelectedLog(log);
    setLogDialogOpen(true);
  };

  const handleGenerateReport = async (id: string) => {
    try {
      const result = await auditService.generateAuditReport(id);
      if (result.success) {
        setSuccess('Raportul a fost generat cu succes');
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Eroare la generarea raportului');
    }
  };

  const handleRunComplianceCheck = async (id: string) => {
    try {
      const result = await auditService.runComplianceCheck(id);
      if (result.success) {
        setSuccess('Verificarea de compliance a fost rulata cu succes');
        await loadComplianceChecks();
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error running compliance check:', err);
      setError('Eroare la rularea verificarii de compliance');
    }
  };

  const getActionIcon = (action: string) => {
    if (action.includes('login') || action.includes('auth')) return <VpnKey />;
    if (action.includes('create') || action.includes('add')) return <Add />;
    if (action.includes('update') || action.includes('edit')) return <Edit />;
    if (action.includes('delete') || action.includes('remove')) return <Delete />;
    if (action.includes('view') || action.includes('read')) return <Visibility />;
    if (action.includes('export') || action.includes('download')) return <Download />;
    if (action.includes('import') || action.includes('upload')) return <CloudUpload />;
    return <Settings />;
  };

  const getResourceIcon = (resourceType: string) => {
    switch (resourceType) {
      case 'user': return <Person />;
      case 'clinic': return <Business />;
      case 'appointment': return <Schedule />;
      case 'payment': return <Payment />;
      case 'patient': return <LocalHospital />;
      case 'inventory': return <Inventory />;
      case 'settings': return <Settings />;
      case 'system': return <Build />;
      default: return <Settings />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'authentication': return 'primary';
      case 'authorization': return 'secondary';
      case 'data_access': return 'info';
      case 'data_modification': return 'warning';
      case 'system': return 'default';
      case 'security': return 'error';
      case 'business': return 'success';
      default: return 'default';
    }
  };

  const getOutcomeColor = (outcome: string) => {
    switch (outcome) {
      case 'success': return 'success';
      case 'failure': return 'error';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getComplianceStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'success';
      case 'non_compliant': return 'error';
      case 'partial': return 'warning';
      case 'not_assessed': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Rapoarte & Audit
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitorizare activitate, compliance si jurnal audit
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              loadAuditLogs();
              loadStats();
              loadReports();
            }}
            disabled={loading}
          >
            Reimprospateaza
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => auditService.exportAuditLogs(filters)}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Success/Error Alerts */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KPICard
              title="Total Activitati"
              value={stats.total}
              icon={<TimelineIcon />}
              color="primary"
              trend={stats.trends.length > 0 ? { value: stats.trends[stats.trends.length - 1].count, isPositive: true } : { value: 0, isPositive: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KPICard
              title="Evenimente Securitate"
              value={stats.securityEvents.failedLogins + stats.securityEvents.privilegeEscalations}
              icon={<Security />}
              color="error"
              trend={{ value: stats.securityEvents.failedLogins + stats.securityEvents.privilegeEscalations, isPositive: false }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KPICard
              title="Rate Succes"
              value={`${((stats.byOutcome.success / stats.total) * 100).toFixed(1)}%`}
              icon={<CheckCircle />}
              color="success"
              trend={{ value: stats.byOutcome.success, isPositive: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KPICard
              title="Erori Critice"
              value={stats.bySeverity.critical + stats.bySeverity.high}
              icon={<ErrorIcon />}
              color="error"
              trend={{ value: stats.bySeverity.critical + stats.bySeverity.high, isPositive: false }}
            />
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="reports tabs" variant="scrollable">
          <Tab label="Jurnal Audit" icon={<TimelineIcon />} iconPosition="start" />
          <Tab label="Statistici" icon={<Assessment />} iconPosition="start" />
          <Tab label="Rapoarte" icon={<Assessment />} iconPosition="start" />
          <Tab label="Reguli Alerta" icon={<Notifications />} iconPosition="start" />
          <Tab label="Compliance" icon={<VerifiedUser />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <LinearProgress sx={{ width: '100%' }} />
        </Box>
      )}

      {/* Jurnal Audit Tab */}
      <TabPanel value={activeTab} index={0}>
        {/* Filtre */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Cauta"
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Categorie</InputLabel>
                <Select
                  value={filters.category || ''}
                  label="Categorie"
                  onChange={(e) => handleFilterChange('category', e.target.value || undefined)}
                >
                  <MenuItem value="">Toate</MenuItem>
                  <MenuItem value="authentication">Autentificare</MenuItem>
                  <MenuItem value="authorization">Autorizare</MenuItem>
                  <MenuItem value="data_access">Acces Date</MenuItem>
                  <MenuItem value="data_modification">Modificare Date</MenuItem>
                  <MenuItem value="system">Sistem</MenuItem>
                  <MenuItem value="security">Securitate</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Severitate</InputLabel>
                <Select
                  value={filters.severity || ''}
                  label="Severitate"
                  onChange={(e) => handleFilterChange('severity', e.target.value || undefined)}
                >
                  <MenuItem value="">Toate</MenuItem>
                  <MenuItem value="low">Scazuta</MenuItem>
                  <MenuItem value="medium">Medie</MenuItem>
                  <MenuItem value="high">Ridicata</MenuItem>
                  <MenuItem value="critical">Critica</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Rezultat</InputLabel>
                <Select
                  value={filters.outcome || ''}
                  label="Rezultat"
                  onChange={(e) => handleFilterChange('outcome', e.target.value || undefined)}
                >
                  <MenuItem value="">Toate</MenuItem>
                  <MenuItem value="success">Succes</MenuItem>
                  <MenuItem value="failure">Esec</MenuItem>
                  <MenuItem value="error">Eroare</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Lista audit logs */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Utilizator</TableCell>
                <TableCell>Actiune</TableCell>
                <TableCell>Resursa</TableCell>
                <TableCell>Categorie</TableCell>
                <TableCell>Severitate</TableCell>
                <TableCell>Rezultat</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditLogs && auditLogs.length > 0 ? auditLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                        {log.userName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {log.userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.userEmail}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getActionIcon(log.action)}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {log.action}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getResourceIcon(log.resourceType)}
                      <Chip
                        label={log.resourceType}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.category}
                      color={getCategoryColor(log.category) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.severity}
                      color={getSeverityColor(log.severity) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.outcome}
                      color={getOutcomeColor(log.outcome) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatForDisplay(log.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Vezi detalii">
                      <IconButton
                        size="small"
                        onClick={() => handleViewLog(log)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nu exista date de afisat
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </TabPanel>

      {/* Statistici Tab */}
      <TabPanel value={activeTab} index={1}>
        {stats && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Activitati pe categorie
                  </Typography>
                  <List>
                    {stats.byCategory && Object.entries(stats.byCategory).map(([category, count]) => (
                      <ListItem key={category}>
                        <ListItemIcon>
                          <Chip
                            label={category}
                            color={getCategoryColor(category) as any}
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={category}
                          secondary={`${count} activitati`}
                        />
                        <ListItemSecondaryAction>
                          <Typography variant="body2" color="text.secondary">
                            {((count / stats.total) * 100).toFixed(1)}%
                          </Typography>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top utilizatori
                  </Typography>
                  <List>
                    {stats.topUsers && stats.topUsers.map((user) => (
                      <ListItem key={user.userId}>
                        <ListItemIcon>
                          <Person />
                        </ListItemIcon>
                        <ListItemText
                          primary={user.userName}
                          secondary={`${user.count} activitati`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* Rapoarte Tab */}
      <TabPanel value={activeTab} index={2}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditingReport(null);
              setReportDialogOpen(true);
            }}
          >
            Adauga Raport
          </Button>
        </Box>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nume</TableCell>
                <TableCell>Tip</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ultima Generare</TableCell>
                <TableCell>Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports && reports.length > 0 ? reports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>{report.name}</TableCell>
                  <TableCell>
                    <Chip label={report.type} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={report.isActive ? 'ACTIV' : 'INACTIV'}
                      color={report.isActive ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {report.lastGenerated ? formatForDisplay(report.lastGenerated) : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Genereaza">
                        <IconButton
                          size="small"
                          onClick={() => handleGenerateReport(report.id)}
                        >
                          <Assessment />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editeaza">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingReport(report);
                            setReportDialogOpen(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sterge">
                        <IconButton size="small">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nu exista rapoarte
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Reguli Alerta Tab */}
      <TabPanel value={activeTab} index={3}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditingAlertRule(null);
              setAlertRuleDialogOpen(true);
            }}
          >
            Adauga Regula
          </Button>
        </Box>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nume</TableCell>
                <TableCell>Descriere</TableCell>
                <TableCell>Conditii</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alertRules && alertRules.length > 0 ? alertRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>{rule.name}</TableCell>
                  <TableCell>{rule.description}</TableCell>
                  <TableCell>
                    <Chip label={rule.conditions.category || 'Toate'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={rule.isActive ? 'ACTIV' : 'INACTIV'}
                      color={rule.isActive ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingAlertRule(rule);
                        setAlertRuleDialogOpen(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton size="small">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nu exista reguli de alerta
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Compliance Tab */}
      <TabPanel value={activeTab} index={4}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditingCompliance(null);
              setComplianceDialogOpen(true);
            }}
          >
            Adauga Verificare
          </Button>
        </Box>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nume</TableCell>
                <TableCell>Standard</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Scor</TableCell>
                <TableCell>Ultima Verificare</TableCell>
                <TableCell>Actiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {complianceChecks && complianceChecks.length > 0 ? complianceChecks.map((check) => (
                <TableRow key={check.id}>
                  <TableCell>{check.name}</TableCell>
                  <TableCell>
                    <Chip label={check.standard} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={check.status.toUpperCase()}
                      color={check.status === 'compliant' ? 'success' : 
                             check.status === 'non_compliant' ? 'error' :
                             check.status === 'partial' ? 'warning' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {check.score}%
                      </Typography>
                      <CircularProgress
                        variant="determinate"
                        value={check.score}
                        size={20}
                        color={check.score >= 80 ? 'success' : check.score >= 60 ? 'warning' : 'error'}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatForDisplay(check.lastChecked)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Ruleaza verificare">
                        <IconButton
                          size="small"
                          onClick={() => handleRunComplianceCheck(check.id)}
                        >
                          <PlayArrow />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editeaza">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingCompliance(check);
                            setComplianceDialogOpen(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sterge">
                        <IconButton size="small">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nu exista verificari de compliance
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>
    </Box>
  );
};

export default ReportsManagement;
